import type { CertProvider, SimulatedCert, WindowsKeyProtection } from './types';

const EIGHTEEN_MONTHS_OUT = new Date(
  Date.now() + 18 * 30 * 24 * 60 * 60 * 1000,
).toISOString();

/** Certificates read off a physical CAC (insert card → pick cert → card PIN). */
export const MOCK_CAC_CERTS: SimulatedCert[] = [
  {
    id: 'sim-cert-identity',
    provider: 'cac',
    purpose: 'identity',
    subjectCN: 'DEMO.SIGNER.1234567890',
    issuerCN: 'DOD ID CA-59',
    notAfter: EIGHTEEN_MONTHS_OUT,
    keyUsage: 'digitalSignature',
    validForActions: [],
  },
  {
    id: 'sim-cert-signing',
    provider: 'cac',
    purpose: 'signing',
    subjectCN: 'DEMO.SIGNER.1234567890',
    issuerCN: 'DOD EMAIL CA-59',
    notAfter: EIGHTEEN_MONTHS_OUT,
    keyUsage: 'nonRepudiation',
    validForActions: ['approve', 'reject'],
  },
  {
    id: 'sim-cert-encryption',
    provider: 'cac',
    purpose: 'encryption',
    subjectCN: 'DEMO.SIGNER.1234567890',
    issuerCN: 'DOD EMAIL CA-59',
    notAfter: EIGHTEEN_MONTHS_OUT,
    keyUsage: 'keyEncipherment',
    validForActions: [],
  },
];

/**
 * Certificates that live in the Windows certificate store (Current User → My).
 * Reached via CryptoAPI/CNG — no card insertion. Key access is mediated by the
 * OS "Windows Security" prompt, whose behavior depends on `keyProtection`.
 */
export const MOCK_WINDOWS_CERTS: SimulatedCert[] = [
  {
    id: 'sim-win-piv-signing',
    provider: 'windows',
    purpose: 'signing',
    friendlyName: 'PIV Signature (Demo)',
    subjectCN: 'DEMO.SIGNER.1234567890',
    issuerCN: 'DEMO ORG ISSUING CA',
    notAfter: EIGHTEEN_MONTHS_OUT,
    keyUsage: 'nonRepudiation',
    keyProtection: 'password',
    validForActions: ['approve', 'reject'],
  },
  {
    id: 'sim-win-hello-signing',
    provider: 'windows',
    purpose: 'signing',
    friendlyName: 'Software Signing (Windows Hello)',
    subjectCN: 'Demo Signer',
    issuerCN: 'DEMO ORG ISSUING CA',
    notAfter: EIGHTEEN_MONTHS_OUT,
    keyUsage: 'digitalSignature, nonRepudiation',
    keyProtection: 'hello',
    validForActions: ['approve', 'reject'],
  },
  {
    id: 'sim-win-email',
    provider: 'windows',
    purpose: 'encryption',
    friendlyName: 'S/MIME Email (Demo)',
    subjectCN: 'demo.signer@example.mil',
    issuerCN: 'DEMO ORG ISSUING CA',
    notAfter: EIGHTEEN_MONTHS_OUT,
    keyUsage: 'keyEncipherment',
    keyProtection: 'none',
    validForActions: [],
  },
];

export function purposeLabel(purpose: SimulatedCert['purpose']): string {
  switch (purpose) {
    case 'identity':
      return 'Identity (PIV Auth)';
    case 'signing':
      return 'Signature';
    case 'encryption':
      return 'Encryption';
  }
}

export function providerLabel(provider: CertProvider): string {
  switch (provider) {
    case 'cac':
      return 'Common Access Card';
    case 'windows':
      return 'Windows certificate store';
  }
}

export function keyProtectionLabel(
  protection: WindowsKeyProtection | undefined,
): string {
  switch (protection) {
    case 'password':
      return 'Strong key protection (password)';
    case 'hello':
      return 'Windows Hello';
    case 'none':
    case undefined:
      return 'No key protection';
  }
}
